import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

declare global {
  interface Window {
    fbq: any;
    ttq: any;
    snaptr: any;
    dataLayer: any[];
  }
}

// Initialize Facebook Pixel
const initFacebookPixel = (pixelId: string) => {
  if (window.fbq || !pixelId) return;
  
  // Create and inject the Facebook Pixel script
  (function(f: any, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode?.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
  
  console.log('✅ Facebook Pixel initialized:', pixelId);
};

// Initialize TikTok Pixel
const initTikTokPixel = (pixelId: string) => {
  if (window.ttq || !pixelId) return;
  
  (function(w: any, d: Document, t: string) {
    w.TiktokAnalyticsObject = t;
    const ttq = w[t] = w[t] || [];
    ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'];
    ttq.setAndDefer = function(t: any, e: string) {
      t[e] = function() {
        t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (let i = 0; i < ttq.methods.length; i++) {
      ttq.setAndDefer(ttq, ttq.methods[i]);
    }
    ttq.instance = function(t: string) {
      const e = ttq._i[t] || [];
      for (let n = 0; n < ttq.methods.length; n++) {
        ttq.setAndDefer(e, ttq.methods[n]);
      }
      return e;
    };
    ttq.load = function(e: string, n?: any) {
      const i = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = i;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      const o = document.createElement('script') as HTMLScriptElement;
      o.type = 'text/javascript';
      o.async = true;
      o.src = i + '?sdkid=' + e + '&lib=' + t;
      const a = document.getElementsByTagName('script')[0];
      a.parentNode?.insertBefore(o, a);
    };
    ttq.load(pixelId);
    ttq.page();
  })(window, document, 'ttq');
  
  console.log('✅ TikTok Pixel initialized:', pixelId);
};

// Initialize Snapchat Pixel
const initSnapchatPixel = (pixelId: string) => {
  if (window.snaptr || !pixelId) return;
  
  (function(e: any, t: Document, n: string) {
    if (e.snaptr) return;
    const a: any = e.snaptr = function() {
      a.handleRequest ? a.handleRequest.apply(a, arguments) : a.queue.push(arguments);
    };
    a.queue = [];
    const s = 'script';
    const r = t.createElement(s) as HTMLScriptElement;
    r.async = true;
    r.src = n;
    const u = t.getElementsByTagName(s)[0];
    u.parentNode?.insertBefore(r, u);
  })(window, document, 'https://sc-static.net/scevent.min.js');
  
  window.snaptr('init', pixelId);
  window.snaptr('track', 'PAGE_VIEW');
  
  console.log('✅ Snapchat Pixel initialized:', pixelId);
};

// Initialize Google Analytics
const initGoogleAnalytics = (measurementId: string) => {
  if (window.dataLayer || !measurementId) return;
  
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) {
    window.dataLayer.push(arguments);
  }
  
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
  
  gtag('js', new Date());
  gtag('config', measurementId);
  
  console.log('✅ Google Analytics initialized:', measurementId);
};

// Inject custom header code (scripts, meta tags, etc.)
const injectCustomHeaderCode = (headerCode: string) => {
  if (!headerCode || headerCode.trim() === '') return;
  
  // Remove existing custom header code container if any
  const existingContainer = document.getElementById('custom-header-code');
  if (existingContainer) {
    existingContainer.remove();
  }
  
  // Create a container to hold the custom code
  const container = document.createElement('div');
  container.id = 'custom-header-code';
  container.style.display = 'none';
  
  // Parse and inject scripts properly
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = headerCode;
  
  // Handle script tags separately (innerHTML doesn't execute scripts)
  const scripts = tempDiv.querySelectorAll('script');
  scripts.forEach((originalScript) => {
    const newScript = document.createElement('script');
    
    // Copy all attributes
    Array.from(originalScript.attributes).forEach(attr => {
      newScript.setAttribute(attr.name, attr.value);
    });
    
    // If script has inline code, copy it
    if (originalScript.textContent) {
      newScript.textContent = originalScript.textContent;
    }
    
    document.head.appendChild(newScript);
  });
  
  // Handle non-script elements (meta, link, style, etc.)
  const nonScripts = tempDiv.querySelectorAll(':not(script)');
  nonScripts.forEach((element) => {
    if (element.tagName) {
      document.head.appendChild(element.cloneNode(true));
    }
  });
  
  console.log('✅ Custom header code injected');
};

export function PixelProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  
  const { data: settings } = useQuery({
    queryKey: ['pixel-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_public_store_settings')
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
  });

  // Initialize pixels and custom code once
  useEffect(() => {
    if (!settings) return;

    if (settings.facebook_pixel_id) {
      initFacebookPixel(settings.facebook_pixel_id);
    }
    if (settings.tiktok_pixel_id) {
      initTikTokPixel(settings.tiktok_pixel_id);
    }
    if (settings.snapchat_pixel_id) {
      initSnapchatPixel(settings.snapchat_pixel_id);
    }
    if (settings.google_analytics_id) {
      initGoogleAnalytics(settings.google_analytics_id);
    }
    // Inject custom header code (Google Analytics code pasted, AdSense, verification tags, etc.)
    if (settings.custom_header_code) {
      injectCustomHeaderCode(settings.custom_header_code);
    }
  }, [settings]);

  // Track page views on route change
  useEffect(() => {
    // Wait a bit for pixels to initialize
    const timer = setTimeout(() => {
      if (window.fbq) {
        window.fbq('track', 'PageView');
      }
      if (window.ttq) {
        window.ttq.page();
      }
      if (window.snaptr) {
        window.snaptr('track', 'PAGE_VIEW');
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return <>{children}</>;
}

// Get currency code from cache or default
const getCurrencyCode = (): string => {
  try {
    const cached = sessionStorage.getItem('store_currency_code');
    if (cached) return cached;
  } catch {}
  return 'EGP';
};

// Export tracking functions for use in components
export const trackEvent = {
  viewContent: (product: { id: string; title: string; price: number; category?: string }) => {
    const currency = getCurrencyCode();
    
    if (window.fbq) {
      window.fbq('track', 'ViewContent', {
        content_ids: [product.id],
        content_name: product.title,
        content_type: 'product',
        value: product.price,
        currency,
      });
    }
    if (window.ttq) {
      window.ttq.track('ViewContent', {
        content_id: product.id,
        content_name: product.title,
        content_type: 'product',
        value: product.price,
        currency,
      });
    }
    if (window.snaptr) {
      window.snaptr('track', 'VIEW_CONTENT', {
        item_ids: [product.id],
        item_category: product.category || 'Product',
        price: product.price,
        currency,
      });
    }
  },

  addToCart: (product: { id: string; title: string; price: number }, quantity: number = 1) => {
    const currency = getCurrencyCode();
    const value = product.price * quantity;
    
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_ids: [product.id],
        content_name: product.title,
        content_type: 'product',
        value,
        currency,
        num_items: quantity,
      });
    }
    if (window.ttq) {
      window.ttq.track('AddToCart', {
        content_id: product.id,
        content_name: product.title,
        content_type: 'product',
        value,
        currency,
        quantity,
      });
    }
    if (window.snaptr) {
      window.snaptr('track', 'ADD_CART', {
        item_ids: [product.id],
        price: value,
        currency,
        number_items: quantity,
      });
    }
  },

  initiateCheckout: (total: number, itemCount: number) => {
    const currency = getCurrencyCode();
    
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        value: total,
        currency,
        num_items: itemCount,
      });
    }
    if (window.ttq) {
      window.ttq.track('InitiateCheckout', {
        value: total,
        currency,
        quantity: itemCount,
      });
    }
    if (window.snaptr) {
      window.snaptr('track', 'START_CHECKOUT', {
        price: total,
        currency,
        number_items: itemCount,
      });
    }
  },

  purchase: (orderId: string, total: number, items: Array<{ id: string; title: string; price: number; quantity: number }>) => {
    const currency = getCurrencyCode();
    const numItems = items.reduce((sum, i) => sum + i.quantity, 0);
    
    if (window.fbq) {
      window.fbq('track', 'Purchase', {
        content_ids: items.map(i => i.id),
        content_type: 'product',
        value: total,
        currency,
        num_items: numItems,
      });
    }
    if (window.ttq) {
      window.ttq.track('CompletePayment', {
        content_type: 'product',
        value: total,
        currency,
        contents: items.map(i => ({
          content_id: i.id,
          content_name: i.title,
          quantity: i.quantity,
          price: i.price,
        })),
      });
    }
    if (window.snaptr) {
      window.snaptr('track', 'PURCHASE', {
        item_ids: items.map(i => i.id),
        price: total,
        currency,
        number_items: numItems,
        transaction_id: orderId,
      });
    }
    
    console.log('🛒 Purchase tracked:', { orderId, total, items });
  },
};
